import { supabase } from "services/supabaseClient";
import { aggregatePrincipalMaturities } from "../lib/allocation";

function requireOrganization(orgId) {
  if (!orgId) throw new Error("Select an organization first.");
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function requireUuid(value, label) {
  if (!UUID_PATTERN.test(String(value || "").trim())) {
    throw new Error(`${label} is missing or is not a valid UUID.`);
  }
}

function throwIfError(error, fallback) {
  if (error) throw new Error(error.message || fallback);
}

export async function fetchProjects(orgId, { includeArchived = false } = {}) {
  requireOrganization(orgId);
  let query = supabase
    .from("cf_projects")
    .select("*")
    .eq("org_id", orgId)
    .order("is_active", { ascending: false })
    .order("project_name", { ascending: true });

  if (!includeArchived) query = query.eq("is_active", true);
  const { data, error } = await query;
  throwIfError(error, "Unable to load Projects.");
  return data || [];
}

export async function saveProject({ project, orgId, tenantId, userId }) {
  requireOrganization(orgId);
  if (!tenantId) throw new Error("The selected organization has no tenant context.");
  if (!userId) throw new Error("A signed-in user is required.");

  const payload = {
    org_id: orgId,
    tenant_id: tenantId,
    project_code: project.project_code?.trim() || null,
    project_name: project.project_name?.trim(),
    description: project.description?.trim() || null,
    status: project.status || "proposed",
    total_project_cost:
      project.total_project_cost === "" || project.total_project_cost == null
        ? null
        : Number(project.total_project_cost),
    start_date: project.start_date || null,
    expected_completion_date: project.expected_completion_date || null,
    actual_completion_date: project.actual_completion_date || null,
    notes: project.notes?.trim() || null,
    is_active: project.status === "archived" ? false : project.is_active !== false,
    updated_by: userId,
  };

  if (!payload.project_name) throw new Error("Project name is required.");

  let result;
  if (project.id) {
    result = await supabase
      .from("cf_projects")
      .update(payload)
      .eq("id", project.id)
      .eq("org_id", orgId)
      .select()
      .single();
  } else {
    result = await supabase
      .from("cf_projects")
      .insert({ ...payload, created_by: userId })
      .select()
      .single();
  }

  throwIfError(result.error, "Unable to save the Project.");
  return result.data;
}

export async function archiveProject({ projectId, orgId, userId }) {
  requireOrganization(orgId);
  const { data, error } = await supabase
    .from("cf_projects")
    .update({ is_active: false, status: "archived", updated_by: userId })
    .eq("id", projectId)
    .eq("org_id", orgId)
    .select()
    .single();
  throwIfError(error, "Unable to archive the Project.");
  return data;
}

export async function deleteProject({ projectId, orgId }) {
  requireOrganization(orgId);
  const componentResult = await supabase
    .from("cf_debt_allocation_components")
    .select("id", { count: "exact", head: true })
    .eq("project_id", projectId)
    .eq("org_id", orgId);
  throwIfError(componentResult.error, "Unable to verify Project references.");
  if (componentResult.count) {
    throw new Error(
      `This Project cannot be deleted because it is referenced by ${componentResult.count} debt allocation component${componentResult.count === 1 ? "" : "s"}. Archive it instead.`
    );
  }

  const assignmentResult = await supabase
    .from("cf_project_classification_assignments")
    .delete()
    .eq("project_id", projectId)
    .eq("org_id", orgId);
  throwIfError(assignmentResult.error, "Unable to remove Project classification assignments.");

  const { error } = await supabase
    .from("cf_projects")
    .delete()
    .eq("id", projectId)
    .eq("org_id", orgId);
  throwIfError(error, "Unable to delete the Project.");
}

export async function fetchClassifications(orgId, { includeArchived = false } = {}) {
  requireOrganization(orgId);
  let classificationQuery = supabase
    .from("cf_classifications")
    .select("*")
    .eq("org_id", orgId)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  let valueQuery = supabase
    .from("cf_classification_values")
    .select("*")
    .eq("org_id", orgId)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });

  if (!includeArchived) {
    classificationQuery = classificationQuery.eq("is_active", true);
    valueQuery = valueQuery.eq("is_active", true);
  }

  const [classificationResult, valueResult, scopeResult] = await Promise.all([
    classificationQuery,
    valueQuery,
    supabase
      .from("cf_classification_scopes")
      .select("*")
      .eq("org_id", orgId)
      .order("sort_order", { ascending: true }),
  ]);

  throwIfError(classificationResult.error, "Unable to load Classifications.");
  throwIfError(valueResult.error, "Unable to load Classification Values.");
  throwIfError(scopeResult.error, "Unable to load Classification scopes.");

  const valuesByClassification = new Map();
  (valueResult.data || []).forEach((value) => {
    const values = valuesByClassification.get(value.classification_id) || [];
    values.push(value);
    valuesByClassification.set(value.classification_id, values);
  });
  const scopesByClassification = new Map();
  (scopeResult.data || []).forEach((scope) => {
    const scopes = scopesByClassification.get(scope.classification_id) || [];
    scopes.push(scope);
    scopesByClassification.set(scope.classification_id, scopes);
  });

  return (classificationResult.data || []).map((classification) => ({
    ...classification,
    values: valuesByClassification.get(classification.id) || [],
    scopes: scopesByClassification.get(classification.id) || [],
  }));
}

export async function saveClassification({ classification, scopes, orgId, tenantId, userId }) {
  requireOrganization(orgId);
  const componentScopeEnabled = (scopes || []).some(
    (scope) => scope.object_type === "debt_allocation_component" && scope.enabled
  );
  const projectScopeEnabled = (scopes || []).some(
    (scope) => scope.object_type === "project" && scope.enabled
  );
  if (componentScopeEnabled && !projectScopeEnabled) {
    throw new Error(
      "A debt allocation component Classification must also be enabled for Projects."
    );
  }
  const payload = {
    org_id: orgId,
    tenant_id: tenantId,
    name: classification.name?.trim(),
    code: classification.code?.trim() || null,
    description: classification.description?.trim() || null,
    is_active: classification.is_active !== false,
    sort_order: Number(classification.sort_order || 0),
    updated_by: userId,
  };
  if (!payload.name) throw new Error("Classification name is required.");

  const result = classification.id
    ? await supabase
        .from("cf_classifications")
        .update(payload)
        .eq("id", classification.id)
        .eq("org_id", orgId)
        .select()
        .single()
    : await supabase
        .from("cf_classifications")
        .insert({ ...payload, created_by: userId })
        .select()
        .single();
  throwIfError(result.error, "Unable to save the Classification.");

  const classificationId = result.data.id;
  const scopePayloads = (scopes || [])
    .filter((scope) => scope.enabled)
    .map((scope, index) => ({
      org_id: orgId,
      tenant_id: tenantId,
      classification_id: classificationId,
      object_type: scope.object_type,
      is_required: !!scope.is_required,
      allow_multiple:
        scope.object_type === "debt_allocation_component"
          ? false
          : !!scope.allow_multiple,
      sort_order: index,
      updated_by: userId,
    }));

  const enabledTypes = scopePayloads.map((scope) => scope.object_type);
  let deleteQuery = supabase
    .from("cf_classification_scopes")
    .delete()
    .eq("classification_id", classificationId)
    .eq("org_id", orgId);
  if (enabledTypes.length) deleteQuery = deleteQuery.not("object_type", "in", `(${enabledTypes.join(",")})`);
  const deleteResult = await deleteQuery;
  throwIfError(deleteResult.error, "Unable to update Classification scopes.");

  if (scopePayloads.length) {
    const scopeResult = await supabase
      .from("cf_classification_scopes")
      .upsert(scopePayloads, { onConflict: "classification_id,object_type" });
    throwIfError(scopeResult.error, "Unable to update Classification scopes.");
  }

  return result.data;
}

export async function archiveClassification({ classificationId, orgId, userId }) {
  requireOrganization(orgId);
  const { data, error } = await supabase
    .from("cf_classifications")
    .update({ is_active: false, updated_by: userId })
    .eq("id", classificationId)
    .eq("org_id", orgId)
    .select()
    .single();
  throwIfError(error, "Unable to archive the Classification.");
  return data;
}

export async function deleteClassification({ classificationId, orgId }) {
  requireOrganization(orgId);
  const referenceChecks = await Promise.all([
    supabase
      .from("cf_classification_values")
      .select("id", { count: "exact", head: true })
      .eq("classification_id", classificationId)
      .eq("org_id", orgId),
    supabase
      .from("cf_project_classification_assignments")
      .select("id", { count: "exact", head: true })
      .eq("classification_id", classificationId)
      .eq("org_id", orgId),
    supabase
      .from("cf_component_classification_assignments")
      .select("id", { count: "exact", head: true })
      .eq("classification_id", classificationId)
      .eq("org_id", orgId),
  ]);
  referenceChecks.forEach((result) =>
    throwIfError(result.error, "Unable to verify Classification references.")
  );

  const [valueResult, projectResult, componentResult] = referenceChecks;
  if (valueResult.count) {
    throw new Error(
      `This Classification cannot be deleted because it contains ${valueResult.count} Classification Value${valueResult.count === 1 ? "" : "s"}. Archive it instead.`
    );
  }
  const assignmentCount = (projectResult.count || 0) + (componentResult.count || 0);
  if (assignmentCount) {
    throw new Error(
      `This Classification cannot be deleted because it is referenced by ${assignmentCount} assignment${assignmentCount === 1 ? "" : "s"}. Archive it instead.`
    );
  }

  const scopeResult = await supabase
    .from("cf_classification_scopes")
    .delete()
    .eq("classification_id", classificationId)
    .eq("org_id", orgId);
  throwIfError(scopeResult.error, "Unable to remove Classification scopes.");

  const { error } = await supabase
    .from("cf_classifications")
    .delete()
    .eq("id", classificationId)
    .eq("org_id", orgId);
  throwIfError(error, "Unable to delete the Classification.");
}

export async function saveClassificationValue({ value, classificationId, orgId, tenantId, userId }) {
  requireOrganization(orgId);
  const payload = {
    org_id: orgId,
    tenant_id: tenantId,
    classification_id: classificationId,
    parent_value_id: value.parent_value_id || null,
    name: value.name?.trim(),
    code: value.code?.trim() || null,
    description: value.description?.trim() || null,
    is_active: value.is_active !== false,
    sort_order: Number(value.sort_order || 0),
    updated_by: userId,
  };
  if (!payload.name) throw new Error("Classification Value name is required.");

  const result = value.id
    ? await supabase
        .from("cf_classification_values")
        .update(payload)
        .eq("id", value.id)
        .eq("org_id", orgId)
        .select()
        .single()
    : await supabase
        .from("cf_classification_values")
        .insert({ ...payload, created_by: userId })
        .select()
        .single();
  throwIfError(result.error, "Unable to save the Classification Value.");
  return result.data;
}

export async function archiveClassificationValue({ valueId, orgId, userId }) {
  requireOrganization(orgId);
  const { data, error } = await supabase
    .from("cf_classification_values")
    .update({ is_active: false, updated_by: userId })
    .eq("id", valueId)
    .eq("org_id", orgId)
    .select()
    .single();
  throwIfError(error, "Unable to archive the Classification Value.");
  return data;
}

export async function deleteClassificationValue({ valueId, orgId }) {
  requireOrganization(orgId);
  const referenceChecks = await Promise.all([
    supabase
      .from("cf_classification_values")
      .select("id", { count: "exact", head: true })
      .eq("parent_value_id", valueId)
      .eq("org_id", orgId),
    supabase
      .from("cf_project_classification_assignments")
      .select("id", { count: "exact", head: true })
      .eq("classification_value_id", valueId)
      .eq("org_id", orgId),
    supabase
      .from("cf_component_classification_assignments")
      .select("id", { count: "exact", head: true })
      .eq("classification_value_id", valueId)
      .eq("org_id", orgId),
  ]);
  referenceChecks.forEach((result) =>
    throwIfError(result.error, "Unable to verify Classification Value references.")
  );

  const [childResult, projectResult, componentResult] = referenceChecks;
  if (childResult.count) {
    throw new Error(
      `This Classification Value cannot be deleted because it has ${childResult.count} child value${childResult.count === 1 ? "" : "s"}. Delete or move those values first.`
    );
  }
  const assignmentCount = (projectResult.count || 0) + (componentResult.count || 0);
  if (assignmentCount) {
    throw new Error(
      `This Classification Value cannot be deleted because it is referenced by ${assignmentCount} assignment${assignmentCount === 1 ? "" : "s"}. Remove those assignments first.`
    );
  }

  const { error } = await supabase
    .from("cf_classification_values")
    .delete()
    .eq("id", valueId)
    .eq("org_id", orgId);
  throwIfError(error, "Unable to delete the Classification Value.");
}

export async function fetchProjectClassificationAssignments(projectId, orgId) {
  if (!projectId) return [];
  requireOrganization(orgId);
  const { data, error } = await supabase
    .from("cf_project_classification_assignments")
    .select("*")
    .eq("project_id", projectId)
    .eq("org_id", orgId);
  throwIfError(error, "Unable to load Project classifications.");
  return data || [];
}

export async function replaceProjectClassificationAssignments({
  projectId,
  assignments,
  orgId,
  tenantId,
  userId,
}) {
  requireOrganization(orgId);
  const existingResult = await supabase
    .from("cf_project_classification_assignments")
    .select("*")
    .eq("project_id", projectId)
    .eq("org_id", orgId);
  throwIfError(existingResult.error, "Unable to load Project classifications.");

  const desired = (assignments || []).flatMap((assignment) =>
    (assignment.classification_value_ids || []).map((valueId) => ({
      org_id: orgId,
      tenant_id: tenantId,
      project_id: projectId,
      classification_id: assignment.classification_id,
      classification_value_id: valueId,
      created_by: userId,
      updated_by: userId,
    }))
  );
  const key = (assignment) =>
    `${assignment.classification_id}:${assignment.classification_value_id}`;
  const desiredKeys = new Set(desired.map(key));
  const existingKeys = new Set((existingResult.data || []).map(key));
  const removeIds = (existingResult.data || [])
    .filter((assignment) => !desiredKeys.has(key(assignment)))
    .map((assignment) => assignment.id);
  const insertRows = desired.filter((assignment) => !existingKeys.has(key(assignment)));

  if (removeIds.length) {
    const deleteResult = await supabase
      .from("cf_project_classification_assignments")
      .delete()
      .in("id", removeIds)
      .eq("org_id", orgId);
    throwIfError(deleteResult.error, "Unable to remove Project classifications.");
  }

  if (!insertRows.length) return existingResult.data || [];

  const { data, error } = await supabase
    .from("cf_project_classification_assignments")
    .insert(insertRows)
    .select();
  throwIfError(error, "Unable to update Project classifications.");
  return data || [];
}

export async function fetchDebtStudioIssues(orgId) {
  requireOrganization(orgId);
  const issueFields = "id,name,status,sharing,deal_class,org_id,tenant_id,par_amount,structure,dated_date,delivery_date,first_coupon_date,final_maturity_date,coupon_frequency,day_count,updated_at";
  const [workspaceResult, libraryResult] = await Promise.all([
    supabase
      .from("ds_deals")
      .select(issueFields)
      .eq("org_id", orgId)
      .order("name", { ascending: true }),
    supabase
      .from("ds_deal_org_access")
      .select(`deal_id,ds_deals:deal_id(${issueFields})`)
      .eq("org_id", orgId),
  ]);
  throwIfError(workspaceResult.error, "Unable to load organization DebtStudio issues.");
  throwIfError(libraryResult.error, "Unable to load library DebtStudio issues.");

  const byId = new Map();
  (workspaceResult.data || []).forEach((issue) => byId.set(issue.id, issue));
  (libraryResult.data || []).forEach((access) => {
    const issue = access.ds_deals;
    if (issue?.id) byId.set(issue.id, issue);
  });

  return [...byId.values()].sort((left, right) =>
    String(left.name || "").localeCompare(String(right.name || ""))
  ).map((issue) => ({
    ...issue,
    isLibrary: String(issue.deal_class || "").toLowerCase() === "library",
    maturities: aggregatePrincipalMaturities(issue.structure),
  }));
}

export async function fetchDebtAllocationPortfolio(orgId) {
  requireOrganization(orgId);
  const componentResult = await supabase
    .from("cf_debt_allocation_components")
    .select("*")
    .eq("org_id", orgId)
    .eq("is_active", true)
    .order("created_at", { ascending: true });
  throwIfError(componentResult.error, "Unable to load the CapitalFlow portfolio.");

  const componentIds = (componentResult.data || []).map((item) => item.id);
  if (!componentIds.length) return { components: [], assignments: [], amounts: [] };
  const [assignmentResult, amountResult] = await Promise.all([
    supabase.from("cf_component_classification_assignments").select("*").in("component_id", componentIds).eq("org_id", orgId),
    supabase.from("cf_debt_allocation_amounts").select("*").in("component_id", componentIds).eq("org_id", orgId),
  ]);
  throwIfError(assignmentResult.error, "Unable to load portfolio classifications.");
  throwIfError(amountResult.error, "Unable to load portfolio allocations.");
  return { components: componentResult.data || [], assignments: assignmentResult.data || [], amounts: amountResult.data || [] };
}

export async function fetchAllProjectClassificationAssignments(orgId) {
  requireOrganization(orgId);
  const { data, error } = await supabase
    .from("cf_project_classification_assignments")
    .select("*")
    .eq("org_id", orgId);
  throwIfError(error, "Unable to load Project classification eligibility.");
  return data || [];
}

export async function fetchDebtAllocationWorkspace({ issueId, orgId }) {
  requireOrganization(orgId);
  if (!issueId) return { components: [], assignments: [], amounts: [] };

  const componentResult = await supabase
    .from("cf_debt_allocation_components")
    .select("*")
    .eq("ds_deal_id", issueId)
    .eq("org_id", orgId)
    .order("created_at", { ascending: true });
  throwIfError(componentResult.error, "Unable to load debt allocation components.");

  const componentIds = (componentResult.data || []).map((component) => component.id);
  if (!componentIds.length) return { components: [], assignments: [], amounts: [] };

  const [assignmentResult, amountResult] = await Promise.all([
    supabase
      .from("cf_component_classification_assignments")
      .select("*")
      .in("component_id", componentIds)
      .eq("org_id", orgId),
    supabase
      .from("cf_debt_allocation_amounts")
      .select("*")
      .in("component_id", componentIds)
      .eq("org_id", orgId),
  ]);
  throwIfError(assignmentResult.error, "Unable to load component classifications.");
  throwIfError(amountResult.error, "Unable to load debt allocation amounts.");

  return {
    components: componentResult.data || [],
    assignments: assignmentResult.data || [],
    amounts: amountResult.data || [],
  };
}

export async function saveDebtAllocationComponent({
  component,
  assignments,
  issueId,
  orgId,
  tenantId,
  userId,
}) {
  requireUuid(orgId, "Organization ID");
  requireUuid(tenantId, "Tenant ID");
  requireUuid(issueId, "DebtStudio Issue ID");
  requireUuid(userId, "User ID");
  if (!component.project_id) throw new Error("Select a Project.");
  requireUuid(component.project_id, "Project ID");
  if (component.id) requireUuid(component.id, "Allocation Component ID");

  (assignments || []).forEach((assignment) => {
    const selectedValueIds = (assignment.classification_value_ids || []).filter(Boolean);
    if (!selectedValueIds.length) return;
    requireUuid(assignment.classification_id, "Classification ID");
    selectedValueIds.forEach((valueId) =>
      requireUuid(valueId, "Classification Value ID")
    );
  });

  const multipleAssignment = (assignments || []).find(
    (assignment) =>
      (assignment.classification_value_ids || []).filter(Boolean).length > 1
  );
  if (multipleAssignment) {
    throw new Error(
      "Select only one value per Classification for an allocation component."
    );
  }

  const projectAssignmentResult = await supabase
    .from("cf_project_classification_assignments")
    .select("classification_id,classification_value_id")
    .eq("project_id", component.project_id)
    .eq("org_id", orgId);
  throwIfError(
    projectAssignmentResult.error,
    "Unable to verify Project classification eligibility."
  );
  const eligible = new Set(
    (projectAssignmentResult.data || []).map(
      (assignment) =>
        `${assignment.classification_id}:${assignment.classification_value_id}`
    )
  );
  const invalidAssignment = (assignments || []).find((assignment) =>
    (assignment.classification_value_ids || []).some(
      (valueId) => !eligible.has(`${assignment.classification_id}:${valueId}`)
    )
  );
  if (invalidAssignment) {
    throw new Error(
      "An allocation component can use only Classification Values enabled for its Project."
    );
  }
  const payload = {
    org_id: orgId,
    tenant_id: tenantId,
    ds_deal_id: issueId,
    project_id: component.project_id,
    component_code: component.component_code?.trim() || null,
    description: component.description?.trim() || null,
    notes: component.notes?.trim() || null,
    is_active: component.is_active !== false,
    updated_by: userId,
  };

  const result = component.id
    ? await supabase
        .from("cf_debt_allocation_components")
        .update(payload)
        .eq("id", component.id)
        .eq("org_id", orgId)
        .select()
        .single()
    : await supabase
        .from("cf_debt_allocation_components")
        .insert({ ...payload, created_by: userId })
        .select()
        .single();
  throwIfError(result.error, "Unable to save the allocation component.");

  const componentId = result.data.id;
  const deleteResult = await supabase
    .from("cf_component_classification_assignments")
    .delete()
    .eq("component_id", componentId)
    .eq("org_id", orgId);
  throwIfError(deleteResult.error, "Unable to update component classifications.");

  const assignmentPayload = (assignments || []).flatMap((assignment) =>
    (assignment.classification_value_ids || []).map((valueId) => ({
      org_id: orgId,
      tenant_id: tenantId,
      component_id: componentId,
      classification_id: assignment.classification_id,
      classification_value_id: valueId,
      created_by: userId,
      updated_by: userId,
    }))
  );
  if (assignmentPayload.length) {
    const assignmentResult = await supabase
      .from("cf_component_classification_assignments")
      .insert(assignmentPayload);
    throwIfError(assignmentResult.error, "Unable to update component classifications.");
  }
  return result.data;
}

export async function deleteDebtAllocationComponent({ componentId, orgId }) {
  requireOrganization(orgId);
  const amountResult = await supabase
    .from("cf_debt_allocation_amounts")
    .delete()
    .eq("component_id", componentId)
    .eq("org_id", orgId);
  throwIfError(amountResult.error, "Unable to delete component allocation amounts.");
  const assignmentResult = await supabase
    .from("cf_component_classification_assignments")
    .delete()
    .eq("component_id", componentId)
    .eq("org_id", orgId);
  throwIfError(assignmentResult.error, "Unable to delete component classifications.");
  const { error } = await supabase
    .from("cf_debt_allocation_components")
    .delete()
    .eq("id", componentId)
    .eq("org_id", orgId);
  throwIfError(error, "Unable to delete the allocation component.");
}

export async function replaceDebtAllocationAmounts({
  issueId,
  orgId,
  tenantId,
  allocations,
}) {
  requireOrganization(orgId);
  const { error } = await supabase.rpc("cf_replace_debt_allocation_amounts", {
    p_org_id: orgId,
    p_tenant_id: tenantId,
    p_ds_deal_id: issueId,
    p_allocations: allocations,
  });
  throwIfError(error, "Unable to save debt allocation amounts.");
}

export function summarizeDebtStudioIssue(issue) {
  const maturities = issue?.maturities || aggregatePrincipalMaturities(issue?.structure);
  return {
    maturityCount: maturities.length,
    principalAmount: maturities.reduce(
      (total, maturity) => total + maturity.principalAmount,
      0
    ),
  };
}
